import type {DesignerAbilitySelectorName} from '@packages/systems/designer-abilities';
import type {PlacementIdentifier} from '@packages/utilities/box';
import type {IconButtonProps} from '@packages/systems/spring';
import type {FeatureFlagName} from '@packages/systems/feature-config/shared';
import {identity} from '@packages/utilities/fp/utils';
import * as React from 'react';
import {PANE_UPDATED, type PanelAction} from './actions';

export type Dispatch = (arg1: {[key: string]: any}) => void;

export type Pane = 'left' | 'right' | 'docked';
export type PanelName = string;
export type PanelPath = Array<PanelName>;

export type PaneUpdate = {
  pane: Pane;
  path?: PanelPath | null;
  panelName: PanelName | null;
  isOpen?: boolean;
  parameters?: Parameters;
};

export type PanelUpdateMeta = {
  closing: Partial<Record<PanelName, boolean>>;
  opening: Partial<Record<PanelName, boolean>>;
};

export type PaneUpdated = {
  type: typeof PANE_UPDATED;
  payload: {
    panels: PanelUpdateMeta;
    updates: Array<Partial<PaneUpdate>>;
  };
};

export type PaneUpdatedAction = {
  type: typeof PANE_UPDATED;
  payload: {
    panels: PanelUpdateMeta;
    metadata?: Metadata;
    updates: Array<Partial<PaneUpdate>>;
  };
  state: any;
};

type LegacyIconProps = {
  active?: boolean;
  className?: string;
  location: string;
  onClick: () => void;
  tooltipPlacement?: PlacementIdentifier;
  ['aria-label']?: string;
  ['aria-labelledby']?: string;
};
type SpringIconProps = {
  ['aria-label']?: string;
  ['aria-labelledby']?: string;
  colorVariant?:
    | 'inherit'
    | 'default'
    | 'primary'
    | 'success'
    | 'danger'
    | 'warning'
    | 'purple';
  contrast?: 'default' | 'low' | 'minimum';
  ['data-automation-id']?: string;
  size?: 8 | 10 | 12 | 14 | 16 | 20 | 24 | 64;
  style?: React.CSSProperties;
};
type RovingTabindexItemProps = {
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  'data-roving-tabindex-item'?: boolean;
  tabIndex?: number;
};
export type IconProps = LegacyIconProps &
  SpringIconProps &
  Pick<IconButtonProps, 'colorVariant' | 'size'> &
  RovingTabindexItemProps & {
    title?: string;
  };

type ContainerProps<PanelState> = {
  automationId?: string;
  dockConfig?: {
    tooltipSuffix: string;
  };
  size?: number | 'large' | 'fullscreen' | 'auto';
  /**
   * Use to replace the default Pane.Head component with Pane.LegacyHead if necessary.
   *
   * All net new panels should use Pane.Head. Existing panels and their children may need to continue to use Pane.LegacyHead.
   */
  PaneHead?: React.ComponentType<any>;
  BeforeTitle?: PanelComponent<PanelState>;
  AfterTitle?: PanelComponent<PanelState>;
  AfterSpace?: PanelComponent<PanelState>;
  HACK_overridePaneProps?: {
    grow?: number;
    style?: React.CSSProperties;
  };
  Note?: PanelComponent<PanelState>;
  shouldCloseOnEsc?: boolean;
  CustomContext?: React.ComponentType<React.PropsWithChildren>;
};

export type PanelTitle<PanelState> =
  | string
  | React.ComponentType<{
      sharedProps?: PanelState;
    }>;

export type PanelComponentProps<PanelState> = {
  sharedProps: PanelState;
  activeChild?: PanelName;
  childPanels?: Array<Panel<any>>;
  closePanel: () => void;
  enabledPanels: Partial<Record<PanelName, Panel<any>>>;
  dispatch: Dispatch;
  parameters: Parameters;
  name?: PanelName;
};

export type PanelComponent<PanelState> = React.ComponentType<
  PanelComponentProps<PanelState>
>;

export type PanelContainerProps<PanelState> = ContainerProps<PanelState> & {
  activeChild?: PanelName;
  childPanels?: Array<Panel<any>>;
  closePanel: () => void;
  dispatch: (arg1?: any) => void;
  name: PanelName;
  enabledPanels: Partial<Record<PanelName, Panel<any>>>;
  Panel: PanelComponent<PanelState>;
  title?: PanelTitle<PanelState>;
  useSharedProps?: PanelStateHook<PanelState>;
  isLastPanel?: boolean;
  parameters: Parameters;
};

type State = any;

type TransitionArgument = {
  dispatch: Dispatch;
  state: State;
  parameters: Parameters;
  from: {
    current?: PanelName | null;
    parameters?: Parameters;
    panel?: PanelName;
    path?: PanelPath;
  };
  to: {
    target?: PanelName | null;
    parameters?: Parameters;
    panel?: PanelName;
    path?: PanelPath;
  };
};

export type PanelTransitionBlocker = (
  arg1: TransitionArgument
) => Promise<
  {redirectTo: {panelName: PanelName; parameters?: Parameters}} | any
>;
export type PanelTransitionSideEffect = (arg1: TransitionArgument) => void;

export type RestrictionOpts = {
  // note: if the panel metadata contains an abilityName setting, it
  // will override any abilityName specified in the shortcut options
  abilityName?: DesignerAbilitySelectorName;
  allowForm?: boolean;
  allowStylePanelInput?: boolean;
  canvasModeOnly?: boolean;
  duringDrag?: boolean;
  additionalRequirements?: Array<() => boolean>;
  skipIfComponentCanvas?: boolean;
  skipIfOpenCanvas?: boolean;
};

export type ResolvedShortcutConfig = PanelShortcutConfig & {name: string};

export type PanelShortcutConfig = {
  categories: Array<'admin' | 'common' | 'design' | 'preview' | 'edit'>;
  combos: Array<string>;
  restrictions?: RestrictionOpts;
};

export type OnAction = (
  action: any,
  parameters: Parameters
) => PanelAction | boolean | undefined;
export type OnActionMap = {
  [actionType: string]: OnAction;
};

export type PanelStateHook<PanelState> = (
  arg1: PanelContainerProps<PanelState>
) => PanelState;

export type SwitchablePanel<PanelState = undefined> =
  | {
      isSwitchablePanel: true;
      abilityName?: undefined;
      featureFlag: FeatureFlagName;
      ifDisabled?: Panel<PanelState> | SwitchablePanel<PanelState>;
      ifEnabled?: Panel<PanelState> | SwitchablePanel<PanelState>;
    }
  | {
      isSwitchablePanel: true;
      abilityName: DesignerAbilitySelectorName;
      featureFlag?: undefined;
      ifDisabled?: Panel<PanelState> | SwitchablePanel<PanelState>;
      ifEnabled?: Panel<PanelState> | SwitchablePanel<PanelState>;
    };

export type Panel<PanelState = undefined> = {
  isSwitchablePanel?: false;
  name: PanelName;
  settingsPanel?: PanelName;
  children?: Array<Panel<any> | SwitchablePanel<any>>;
  Icon?: React.ComponentType<IconProps>;
  Panel: PanelComponent<PanelState>;
  Wrapper?: React.ComponentType<
    React.PropsWithChildren<{
      parameters?: Parameters;
      dispatch: Dispatch;
    }>
  >;
  useSharedProps?: PanelStateHook<PanelState>;
  metadata: {
    large?: boolean;
    hidden?: boolean;
    abilityName?: DesignerAbilitySelectorName;
    forwardToChild?: boolean | (() => boolean);
    chromeless?: boolean;
    menuItemTutorialPopoverId?: string;
    parameter?: string;
    shortcut?: PanelShortcutConfig;
    title?: PanelTitle<PanelState>;
    AfterListTitle?: React.ComponentType<Record<any, any>>;
    panelPrefix?: string;
    /**
     * Declares that this panel has no use for the AI Assistant — its entry
     * point stays hidden and its panel closes while this panel is active.
     * A generic flag other consumers read declaratively, same as
     * `abilityName`; this package has no idea what "AI Assistant" is.
     */
    suppressesAiAssistant?: boolean;
    /**
     * Declares that this panel takes over the whole canvas, so the customer is
     * no longer working on a page — page-scoped chrome (page switcher, page
     * publishing status) does not apply while this panel, or any of its
     * children, is active. A generic flag other consumers read declaratively,
     * same as `suppressesAiAssistant`; this package has no idea what a page is.
     */
    suppressesPageContext?: boolean;
  };
  containerProps?: ContainerProps<PanelState>;
  lifecycles?: {
    onAction?: OnActionMap;
    onEnterRequested?: PanelTransitionBlocker;
    onEnterCompleted?: PanelTransitionSideEffect;
    onExitRequested?: PanelTransitionBlocker;
    onExitCompleted?: PanelTransitionSideEffect;
  };
};

export type ResolvedPanel<PanelState = undefined> = Omit<
  Panel<PanelState>,
  'children'
> & {
  children: Array<ResolvedPanel<any>>;
};

export type InternalPanel = ResolvedPanel<any> & {
  byName: Partial<Record<PanelName, InternalPanel>>;
};

export type Panels = {
  all: Partial<Record<PanelName, ResolvedPanel<any>>>;
  array: Array<ResolvedPanel<any>>;
  byName: Partial<Record<PanelName, InternalPanel>>;
  onAction: OnActionMap;
  paths: Partial<Record<PanelName, PanelPath>>;
  shortcuts: Array<ResolvedShortcutConfig>;
};

export type PanelTransition = {
  lifecycles: {
    blockers: Array<(dispatch: Dispatch, state: State) => Promise<any>>;
    sideEffects: Array<(dispatch: Dispatch, state: State) => void>;
  };
  panels: PanelUpdateMeta;
  parameters: Parameters;
};

type PendingDock = {
  type: 'dock';
  docked: {
    panelName: PanelName | null;
  };
  target: {
    pane: Pane;
    panelName: PanelName | null;
  };
};

type PendingFocusDock = {
  type: 'focusDock';
  docked: {
    panelName: PanelName | null;
  };
  target: {
    pane: Pane;
    panelName: PanelName | null;
  };
};

type PendingUndock = {
  type: 'undock';
  docked: {
    panelName: PanelName | null;
  };
  target: {
    pane: Pane;
    panelName: PanelName | null;
  };
};

type PendingToggle = {
  type: 'toggle';
  pane: Pane;
  panelName: PanelName | null;
  isOpen: boolean;
};

type PendingTransition = {
  type: 'transition';
  pane: Pane;
  from: {
    panelName: PanelName | null;
    parameters?: Parameters;
  };
  to: {
    panelName: PanelName | null;
    parameters?: Parameters;
  };
};

type WindowResize = {
  type: 'windowResize';
  docked: {
    canDock: boolean;
    panelName: PanelName | null;
  };
  target: {
    pane: Pane;
    panelName: PanelName | null;
  };
};

export type Parameters = Record<string, any>;
export type Metadata = Record<string, any>;

type WithMetadata<Update> = Update & {
  metadata: Metadata;
};

export type PendingUpdate =
  | WithMetadata<PendingDock>
  | WithMetadata<PendingFocusDock>
  | WithMetadata<PendingUndock>
  | WithMetadata<PendingToggle>
  | WithMetadata<PendingTransition>
  | WithMetadata<WindowResize>;

export type InternalPanelsState = {
  paneState: Partial<
    Record<
      Pane,
      {
        panelName: PanelName | null;
        isOpen?: boolean;
      }
    >
  >;
  panelConfigs: Array<Panel<any> | SwitchablePanel<any>>;
  panels: Panels;
  parameters: Parameters;
  pending?: PendingUpdate;
  rejected?: PendingUpdate;
};

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type OpaquePanelsState = InternalPanelsState;

export function getPaneState(state: OpaquePanelsState) {
  return state.paneState;
}

export const unwrap: (arg1: OpaquePanelsState) => InternalPanelsState =
  identity;
export const wrap: (arg1: InternalPanelsState) => OpaquePanelsState = identity;

type EngineUpdate<GlobalState> = {
  globalState: GlobalState;
  panelsState: InternalPanelsState;
};
export type Engine<GlobalState> = (
  arg1: Dispatch
) => (arg1: EngineUpdate<GlobalState>) => void;
